import { type TableMetadata, type TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';

import { type SeveridadeValidade } from './validade-solicitacao';

/**
 * A linha que as três abas entregam à tabela.
 *
 * As três montam exatamente a mesma forma nos seus getters; declará-la aqui, ao lado das colunas
 * que a consomem, é o que permite o cartão receber `rowData` tipado em vez do `any` que o
 * `ng-template` entrega.
 */
export type LinhaTransferencia = {
    idSolicitacaoStr: string;
    crmUfChip: string;
    medicoPerfil: TablePerfilValue;
    cpf?: string;
    statusStr: string;
    dataSolicitacaoStr: string;
    dataValidadeTag: { value: string; severity: SeveridadeValidade };
    tipoSolicitacaoTag: { value: string; severity: string };
};

/**
 * Metadata das tabelas da Central de Transferência.
 *
 * <p>Fora dos componentes de propósito, e uma só para as três telas (abertas por origem, por destino
 * e aguardando encerramento): a declaração de colunas é configuração, não comportamento, e as três
 * listam a MESMA solicitação — divergir no formato faria a mesma informação se ler de um jeito
 * diferente em cada aba.
 *
 * <p>Descreve só a visão de TABELA. O CARD é sob medida (`app-cartao-transferencia`, ligado por
 * `useCustomCard`), então nada aqui o alcança — nem `hiddenOnCard`, nem `sub`, nem coluna declarada
 * só para ele. Enquanto o card era declarativo, esta metadata tinha entradas que existiam só para
 * ele; elas saíram junto, senão ficariam descrevendo uma tela que não é mais desenhada assim.</p>
 *
 * <p>Segue o molde do Acompanhamento (comissões):
 * <ul>
 *   <li><b>Declarativas com `sub`</b> — identificação (id + CPF), registro (CRM + UF) e datas
 *       (solicitação + validade): valor principal em cima, linha secundária embaixo. Antes eram seis
 *       colunas de uma linha cada, que é o que espalhava a tabela.</li>
 *   <li><b>Perfil</b> — o médico, com retrato e nome, pelo tipo `perfil` do próprio CustomTable.</li>
 *   <li><b>`enfase.maquina`</b> — id, CPF, CRM e datas são dado gerado por máquina e ganham a
 *       tipografia tabular, que alinha os dígitos entre as linhas.</li>
 * </ul>
 *
 * <p>É função, e não constante: o `hidden` é reescrito pelo seletor de colunas, e uma constante
 * compartilhada faria as três telas disputarem o mesmo estado de visibilidade.
 *
 * @param rotuloUf cabeçalho da UF que a tela destaca — "UF Destino" na origem, "UF Origem" no destino.
 * @param chaveUf campo da linha que alimenta essa UF.
 */
export function construirMetadataTransferencias(rotuloUf: string, chaveUf: string): TableMetadata {
    return {
        headers: {
            // Aponta para `medicoPerfil`, e não para `nomeMedico`: o segundo segue sendo o nome cru,
            // que o card e o expandido usam. Um campo por forma de leitura.
            //
            // A inscrição desce como `sub` num token só — `CRM 5224/DF`, o mesmo formato do card do
            // kanban. Ela IDENTIFICA o médico, e ao lado do nome se lê como uma coisa só.
            //
            // A UF daqui é a DELE, nunca a do movimento: "CRM 4343" seguido da UF de destino afirma
            // que o médico é inscrito num conselho onde ele ainda não está. O destino é dado do
            // pedido, e por isso tem coluna própria.
            // `celulaCustomizada`: o tipo `perfil` desenha retrato e nome e nada mais, e aqui o
            // protocolo vai num badge ACIMA do nome — sem equivalente declarativo. O desenho vive em
            // `app-identificacao-pessoa`, um bloco só para as três abas.
            medicoPerfil: { type: 'text', value: 'Médico', hidden: false, default: 'Não informado', celulaCustomizada: true },
            cpf: { type: 'text', value: 'CPF', hidden: false, default: '-', enfase: { maquina: true } },
            // `celulaCustomizada`: a UF vem com a bandeira do estado ao lado da sigla
            // (`app-bandeira-uf`), e não há tipo declarativo que desenhe imagem-mais-texto numa
            // célula — `icon` só aceita glifo de fonte. Como a metadata é uma só, as três abas
            // precisam declarar o `@case` da SUA chave de UF no `pTemplate="cell"`; sem ele a
            // coluna sairia vazia.
            [chaveUf]: { type: 'text', value: rotuloUf, hidden: false, default: '-', celulaCustomizada: true },
            tipoSolicitacaoTag: { type: 'tag', value: 'Tipo', hidden: false, default: '-' },
            dataSolicitacaoStr: {
                type: 'text',
                value: 'Solicitação e validade',
                hidden: false,
                default: '-',
                enfase: { forte: true, maquina: true },
                celulaCustomizada: true
            },
            statusStr: { type: 'text', value: 'Situação', hidden: false, default: '-' }
        },
        title: 'nomeMedico',
        tag: 'tipoSolicitacaoTag'
    };
}
